import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { useAuth } from './authContext';
import supabase from '../supabaseClient';

const MessagesUnreadContext = createContext(null);

export const useMessagesUnread = () => useContext(MessagesUnreadContext);

export const MessagesUnreadProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCounts, setUnreadCounts] = useState({});
  const [isFetching, setIsFetching] = useState(false);
  const lastFetchRef = useRef(0);

  const fetchUnreadCountsRPC = async () => {
    if (!user?.id) {
      setUnreadCounts({});
      return;
    }
    const now = Date.now();
    // Simple guard to avoid overlapping or excessively frequent fetches
    if (isFetching && now - lastFetchRef.current < 2000) return;
    lastFetchRef.current = now;
    setIsFetching(true);
    try {
      const { data, error } = await supabase.rpc('get_unread_counts', { uid: user.id });
      if (error) {
        console.error('get_unread_counts error', error);
        return;
      }
      const byId = {};
      (data || []).forEach(row => {
        byId[row.id] = row.unread_count || 0;
      });
      setUnreadCounts(byId);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!user?.id) {
      setUnreadCounts({});
      return;
    }
    let cancelled = false;
    const run = async () => {
      if (cancelled) return;
      await fetchUnreadCountsRPC();
    };
    run();
    // Poll at a modest interval; realtime events still bump counts eagerly
    const intervalId = setInterval(run, 45000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user?.id]);

  const bumpUnreadForIncoming = (contactId) => {
    if (!contactId) return;
    setUnreadCounts(prev => ({ ...prev, [contactId]: (prev[contactId] || 0) + 1 }));
  };

  const markThreadRead = async (contactId, type = 'direct') => {
    if (!user?.id || !contactId) return;
    const now = new Date().toISOString();
    const isGroup = type === 'group' || type === 'academy_group';

    if (isGroup) {
      const { data: existing } = await supabase
        .from('message_reads')
        .select('id')
        .eq('user_id', user.id)
        .eq('conversation_id', contactId)
        .maybeSingle();
      if (existing) {
        await supabase.from('message_reads').update({ last_read_at: now }).eq('id', existing.id);
      } else {
        await supabase.from('message_reads').insert({
          user_id: user.id,
          contact_id: null,
          conversation_id: contactId,
          last_read_at: now,
        });
      }
    } else {
      const { data: existing } = await supabase
        .from('message_reads')
        .select('id')
        .eq('user_id', user.id)
        .eq('contact_id', contactId)
        .maybeSingle();
      if (existing) {
        await supabase.from('message_reads').update({ last_read_at: now }).eq('id', existing.id);
      } else {
        await supabase.from('message_reads').insert({
          user_id: user.id,
          contact_id: contactId,
          conversation_id: null,
          last_read_at: now,
        });
      }
    }

    setUnreadCounts(prev => ({ ...prev, [contactId]: 0 }));
  };

  const markThreadUnread = async (contact) => {
    if (!user?.id || !contact?.id) return;
    const contactId = contact.id;
    const isGroup = contact.type === 'group' || contact.type === 'academy_group';

    if (isGroup) {
      const { data: existing } = await supabase
        .from('message_reads')
        .select('id')
        .eq('user_id', user.id)
        .eq('conversation_id', contactId)
        .maybeSingle();
      if (existing) {
        await supabase.from('message_reads').update({ last_read_at: '1970-01-01T00:00:00Z' }).eq('id', existing.id);
      } else {
        await supabase.from('message_reads').insert({
          user_id: user.id,
          contact_id: null,
          conversation_id: contactId,
          last_read_at: '1970-01-01T00:00:00Z',
        });
      }
    } else {
      const { data: existing } = await supabase
        .from('message_reads')
        .select('id')
        .eq('user_id', user.id)
        .eq('contact_id', contactId)
        .maybeSingle();
      if (existing) {
        await supabase.from('message_reads').update({ last_read_at: '1970-01-01T00:00:00Z' }).eq('id', existing.id);
      } else {
        await supabase.from('message_reads').insert({
          user_id: user.id,
          contact_id: contactId,
          conversation_id: null,
          last_read_at: '1970-01-01T00:00:00Z',
        });
      }
    }

    await fetchUnreadCountsRPC();
  };

  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((sum, n) => sum + (n || 0), 0),
    [unreadCounts]
  );

  const value = useMemo(
    () => ({
      unreadCounts,
      totalUnread,
      refreshUnreadCounts: fetchUnreadCountsRPC,
      bumpUnreadForIncoming,
      markThreadRead,
      markThreadUnread,
    }),
    [unreadCounts, totalUnread]
  );

  return (
    <MessagesUnreadContext.Provider value={value}>
      {children}
    </MessagesUnreadContext.Provider>
  );
};
